<template>
  <BModal
    modal-name="bulkUpdateLeadSources"
    width="550px"
    height="490px"
    :delegate="modalDelegate"
  >
    <BHeightAdjuster>
      <BModalHeader
        center
        class="modal-header"
        :title="$t('leadSource.bulkUpdate.title')"
        @modal-close="handleClose"
      />
      <BModalBody class="modal-body">
        <BulkUpdateModalTarget
          :label="$t('leadSource.bulkUpdate.target')"
          :count="selectedCallTargetIds.length"
        />
        <BListItem class="mb-300">
          <template #header>
            <div>{{ $t('leadSource.bulkUpdate.modeLabel') }}</div>
          </template>
          <BLayout align-center>
            <BRadio
              v-model="mode"
              label="add"
              class="mr-300"
            >
              {{ $t('leadSource.bulkUpdate.modeAdd') }}
            </BRadio>
            <BRadio
              v-model="mode"
              label="delete"
            >
              {{ $t('leadSource.bulkUpdate.modeDelete') }}
            </BRadio>
          </BLayout>
        </BListItem>
        <BListItem>
          <template #header>
            <div>{{ $t('leadSource.bulkUpdate.selectLabel') }}</div>
          </template>
          <BSelect
            v-model="selectedLeadSourceIds"
            class="lead-source-select"
            :items="leadSourcesCandidates"
            item-text="label"
            item-value="value"
            :placeholder="$t('leadSource.bulkUpdate.selectPlaceholder')"
            multiple
            filterable
          />
        </BListItem>
        <BulkUpdateOverLimitMessage
          :visible="isOverLimit"
          :limit="BULK_UPDATE_LIMIT"
        />
      </BModalBody>
      <BulkUpdateModalFooter
        :loading="isLoading"
        :disabled="isUpdateDisabled"
        @cancel="handleClose"
        @submit="submitBulkUpdateLeadSources"
      />
    </BHeightAdjuster>
  </BModal>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { CallTargetApiService } from '@/api/user/resources/call_target';
import BulkUpdateModalFooter from '@/components/organisms/user/leadList/BulkUpdateModalFooter.vue';
import BulkUpdateModalTarget from '@/components/organisms/user/leadList/BulkUpdateModalTarget.vue';
import BulkUpdateOverLimitMessage from '@/components/organisms/user/leadList/BulkUpdateOverLimitMessage.vue';
import { useConfirm } from '@/composable/message-dialog';
import { useVuexLeadSources } from '@/composable/user/leadSource/lead-source';
import { useWait } from '@/composable/vue-wait';
import { KEY_OF_MODAL, KEY_OF_TOAST } from '@/injection-keys';
import { key } from '@/store/index';

type TProps = {
  selectedCallTargetIds: number[];
};
type TMode = 'add' | 'delete';

const props = defineProps<TProps>();

const MODAL_NAME = 'bulkUpdateLeadSources';
const BULK_UPDATE_LIMIT = 100;
const i18n = useI18n();
const store = useStore(key);
const modal = inject(KEY_OF_MODAL);
const toast = inject(KEY_OF_TOAST);
const { wait, doActionWithWait } = useWait();
const { confirm } = useConfirm();
const { leadSourcesCandidates, fetchAll } = useVuexLeadSources();
const api = new CallTargetApiService();

const mode = ref<TMode>('add');
const selectedLeadSourceIds = ref<number[]>([]);

onMounted(() => {
  fetchAll();
});

const isOverLimit = computed<boolean>(() => props.selectedCallTargetIds.length > BULK_UPDATE_LIMIT);
const isUpdateDisabled = computed<boolean>(() => selectedLeadSourceIds.value.length === 0 || isOverLimit.value);
const isLoading = computed<boolean>(() => wait.is(['bulkUpdateLeadSourcesWait']));

const modalDelegate = {
  beforeClose: () => {
    mode.value = 'add';
    selectedLeadSourceIds.value = [];
  },
};

const handleClose = () => {
  modal.hide(MODAL_NAME);
};

const selectedLeadSourceNames = computed<string>(() =>
  leadSourcesCandidates.value
    .filter((candidate) => selectedLeadSourceIds.value.includes(candidate.value as number))
    .map((candidate) => `「${candidate.label}」`)
    .join(''),
);

const submitBulkUpdateLeadSources = async () => {
  const count = props.selectedCallTargetIds.length;
  const confirmMessageKey = mode.value === 'add' ? 'leadSource.bulkUpdate.confirmAdd' : 'leadSource.bulkUpdate.confirmDelete';
  const confirmed = await confirm({
    message: i18n.t(confirmMessageKey, { count, sources: selectedLeadSourceNames.value }),
  });
  if (!confirmed) return;

  let succeeded = false;
  await doActionWithWait('bulkUpdateLeadSourcesWait', async () => {
    try {
      const request = {
        bulkLeadSourcesBody: {
          callTargetIds: props.selectedCallTargetIds,
          leadSourceIds: selectedLeadSourceIds.value,
        },
      };
      if (mode.value === 'add') {
        await api.bulkAppendLeadSources({ request, errorHandlers: { disableDefaultErrorHandler: true } });
      } else {
        await api.bulkDeleteLeadSources({ request, errorHandlers: { disableDefaultErrorHandler: true } });
      }
      succeeded = true;
      const doneMessageKey = mode.value === 'add' ? 'leadSource.bulkUpdate.doneAdd' : 'leadSource.bulkUpdate.doneDelete';
      toast.show(i18n.t(doneMessageKey));
    } catch {
      toast.show({ message: i18n.t('leadSource.bulkUpdate.failed'), type: 'error' });
    }
  });
  if (!succeeded) return;

  handleClose();
  await getCallList();
};

const getCallList = async () => {
  // UX改善のため、データ再読込時に全体ローディングしないようになっている
  toast.show({ message: i18n.t('callList.update.loading'), type: 'info' });
  await doActionWithWait('getCallTargetsBackgroundWait', async () => {
    await store.dispatch('user/getCallTargetsAction');
  });
  toast.show({ message: i18n.t('callList.update.done'), type: 'success' });
};
</script>

<style lang="scss" scoped>
  .modal-body {
    padding: $basespace-600;
  }

  .lead-source-select {
    width: 100%;
  }
</style>
